import { useEffect, useMemo, useRef, useState } from "react";
import { Star } from "lucide-react";
import { transformations, transformationFilters } from "@/data/transformations";
import { Section } from "./Section";
import { Reveal } from "./Reveal";

const Stars = () => (
    <div className="flex gap-1" aria-hidden="true">
        {[...Array(5)].map((_, i) => (
            <Star key={i} size={12} className="fill-rz-red text-rz-red" />
        ))}
    </div>
);

const Card = ({ t }) => (
    <article
        data-testid={`transformation-card-${t.id}`}
        className="rz-corners w-[82vw] max-w-[520px] shrink-0 select-none border border-rz-steel/60 bg-rz-graphite/40 sm:w-[520px]"
    >
        <div className="rz-scan relative">
            <img
                src={t.image}
                alt={t.alt}
                loading="lazy"
                decoding="async"
                draggable="false"
                className="aspect-[4/3] w-full object-cover"
            />
        </div>
        <div className="rz-ticks" aria-hidden="true" />
        <div className="p-6">
            <Stars />
            <p className="mt-4 font-display text-[10px] uppercase tracking-[0.25em] text-rz-red">
                {t.service}
            </p>
            <h3 className="mt-1.5 font-display text-xl font-bold uppercase tracking-wide text-rz-white">
                {t.vehicle}
            </h3>
            <p className="mt-2.5 text-sm italic leading-relaxed text-rz-white/85">"{t.caption}"</p>
            {t.sub && <p className="mt-2 text-[13px] leading-relaxed text-rz-smoke">{t.sub}</p>}
            <p className="mt-4 border-t border-rz-steel/60 pt-3 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-rz-white/90">
                {t.reaction}
            </p>
        </div>
    </article>
);

export const Transformations = () => {
    const [filter, setFilter] = useState("all");
    const trackRef = useRef(null);
    const pos = useRef(0);
    const hovering = useRef(false);
    const dragging = useRef(false);
    const lastX = useRef(0);

    const items = useMemo(() => {
        const base =
            filter === "all"
                ? transformations
                : transformations.filter((t) => t.category === filter);
        return base.length < 3 ? [...base, ...base, ...base, ...base] : base;
    }, [filter]);

    useEffect(() => {
        pos.current = 0;
    }, [filter]);

    useEffect(() => {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let raf;
        let last = performance.now();
        const step = (now) => {
            const dt = Math.min((now - last) / 1000, 0.05);
            last = now;
            const track = trackRef.current;
            if (track) {
                const half = track.scrollWidth / 2;
                if (half > 0) {
                    if (!reduce && !hovering.current && !dragging.current) pos.current += 26 * dt;
                    pos.current = ((pos.current % half) + half) % half;
                    track.style.transform = `translateX(${-pos.current}px)`;
                }
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [items]);

    const onPointerDown = (e) => {
        dragging.current = true;
        lastX.current = e.clientX;
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!dragging.current) return;
        pos.current -= e.clientX - lastX.current;
        lastX.current = e.clientX;
    };
    const endDrag = () => {
        dragging.current = false;
    };

    return (
        <Section id="transformations" index="01" label="Proof" title="See the transformation.">
            <Reveal className="mt-6" delay={0.05}>
                <p className="font-display text-sm font-semibold uppercase tracking-[0.22em] text-rz-white">
                    Real cars. <span className="text-rz-red">Real work.</span> Real results.
                </p>
                <p className="mt-3 max-w-[60ch] text-base leading-relaxed text-rz-smoke">
                    From protection and paint refinement to complete interior restoration and custom
                    transformations — see the difference for yourself.
                </p>
            </Reveal>

            <Reveal className="mt-8" delay={0.08}>
                <div className="flex flex-wrap gap-2.5">
                    {transformationFilters.map((f) => (
                        <button
                            key={f.id}
                            data-testid={`transformation-filter-${f.id}`}
                            onClick={() => setFilter(f.id)}
                            aria-pressed={filter === f.id}
                            className={`border px-4 py-2 font-display text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                                filter === f.id
                                    ? "border-rz-red text-rz-white"
                                    : "border-rz-steel text-rz-smoke hover:border-rz-smoke hover:text-rz-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </Reveal>

            <Reveal className="mt-10" delay={0.1}>
                <div
                    data-testid="transformation-gallery"
                    className="relative -mx-5 cursor-grab touch-pan-y overflow-hidden active:cursor-grabbing sm:-mx-8 lg:-mx-12"
                    onMouseEnter={() => (hovering.current = true)}
                    onMouseLeave={() => (hovering.current = false)}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                >
                    <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-rz-black to-transparent sm:w-20" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-rz-black to-transparent sm:w-20" />
                    <div ref={trackRef} className="flex w-max gap-6 will-change-transform">
                        {[...items, ...items].map((t, i) => (
                            <Card key={`${t.id}-${i}`} t={t} />
                        ))}
                    </div>
                </div>
                <p className="mt-5 font-display text-[10px] uppercase tracking-[0.22em] text-rz-smoke">
                    Drag or hover to pause — real Revautoz studio photography
                </p>
            </Reveal>
        </Section>
    );
};
